import React, { useState, useEffect } from 'react';
import Alert from '@/Components/Alert.jsx';
import { isWithinInterval, parse } from 'date-fns';
import { calculateNextActiveRange } from '@/Utils/alertDateLogic.jsx';
import Cookies from 'js-cookie';
import InfoIconWithPopover from '@/Components/MoreInfoPopover.jsx';
import {
  useStudentEmailNotVerifiedAlert,
  useStudentEmailVerifiedAlert,
} from '@/Hooks/useAlert.jsx';

const AlertsContainer = () => {
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [edu1Visible, setEdu1Visible] = useStudentEmailVerifiedAlert();
  const [edu2Visible, setEdu2Visible] = useStudentEmailNotVerifiedAlert();
  const [paymentAlertVisible, setPaymentAlertVisible] = useState(
    !!localStorage.getItem('stripe_id')
  );

  // Handle scroll event to show sticky alerts
  useEffect(() => {
    const handleScroll = () => {
      // Show alerts after scrolling down 100px
      setHasScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await axios.get('/alerts');
        const currentDate = new Date();

        // Get dismissed alert IDs from cookies
        const dismissedAlerts = Cookies.get('dismissedAlerts');
        const dismissedAlertIds = dismissedAlerts ? dismissedAlerts.split(',') : [];

        // Filter alerts based on date range and dismissed state
        const filteredAlerts = response.data.filter((alert) => {
          if (dismissedAlertIds.includes(alert.id.toString())) {
            return false; // Skip dismissed alerts
          }

          const range = calculateNextActiveRange(alert);
          if (!range) return false; // Exclude alerts with invalid ranges

          const start = parse(range.start, 'MM/dd/yyyy', new Date());
          const end = parse(range.end, 'MM/dd/yyyy', new Date());

          return isWithinInterval(currentDate, { start, end });
        });

        setAlerts(filteredAlerts);
      } catch (error) {
        console.error('Error fetching alerts:', error);
      } finally {
        setAlertsLoading(false);
      }
    };

    fetchAlerts();
  }, []);

  const handleAlertClose = (alertId) => {
    // Add the alert ID to cookies
    const dismissedAlerts = Cookies.get('dismissedAlerts');
    const dismissedAlertIds = dismissedAlerts ? dismissedAlerts.split(',') : [];
    if (!dismissedAlertIds.includes(alertId.toString())) {
      dismissedAlertIds.push(alertId);
    }
    Cookies.set('dismissedAlerts', dismissedAlertIds.join(','), { expires: 365 });

    // Remove the alert from the state
    setAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
  };

  return (
    <>
      {/* Desktop alerts (normal behavior) */}
      {!alertsLoading && (
        <div className="alerts-container hidden md:block w-full">
          {alerts.map((alert) => (
            <Alert
              key={alert.id}
              id={`alert-${alert.id}`}
              type="success" // Or adjust dynamically if needed
              title={alert.title}
              message={<span dangerouslySetInnerHTML={{ __html: alert.message }} />}
              visible={true}
              onClose={() => handleAlertClose(alert.id)}
            />
          ))}
        </div>
      )}

      {/* Mobile sticky alerts (appear after scrolling) */}
      {!alertsLoading && (
        <div
          className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 md:hidden ${
            hasScrolled ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          {alerts.map((alert) => (
            <Alert
              key={alert.id}
              id={`mobile-alert-${alert.id}`}
              type="success"
              title={alert.title}
              message={<span dangerouslySetInnerHTML={{ __html: alert.message }} />}
              visible={true}
              onClose={() => handleAlertClose(alert.id)}
            />
          ))}
        </div>
      )}

      {/* Desktop hardcoded alerts */}
      <div className="hidden md:block">
        <Alert
          id="edu1Alert"
          type="success"
          title="Student Discount Activated"
          message={
            <>
              you'll receive{' '}
              <InfoIconWithPopover
                text="50% off"
                title="Student Discount"
                content="We know money can be tight so we're offering 50% off for students at checkout!"
              />
              , courtesy of your university!
            </>
          }
          visible={edu1Visible}
          onClose={() => setEdu1Visible(false)}
        />
        <Alert
          id="edu2Alert"
          type="info"
          title="Student Discount Available"
          message={
            <>
              <a href={route('verification.notice')} className="underline">
                Verify your email
              </a>{' '}
              and you'll receive 50% off, courtesy of your university!
            </>
          }
          visible={edu2Visible}
          onClose={() => setEdu2Visible(false)}
        />
        <Alert
          id="payment"
          type="error"
          title="Finish your payment"
          message={
            <>
              {' '}
              Please{' '}
              <a
                href={route('payment', { id: localStorage.getItem('stripe_id') })}
                className="underline"
              >
                Click here
              </a>{' '}
              to apply your payment to your account!
            </>
          }
          visible={paymentAlertVisible}
          onClose={() => {}}
        />
      </div>

      {/* Mobile sticky hardcoded alerts */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 md:hidden ${
          hasScrolled ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <Alert
          id="edu1Alert-mobile"
          type="success"
          title="Student Discount Activated"
          message={
            <>
              you'll receive{' '}
              <InfoIconWithPopover
                text="50% off"
                title="Student Discount"
                content="We know money can be tight so we're offering 50% off for students at checkout!"
              />
              , courtesy of your university!
            </>
          }
          visible={edu1Visible}
          onClose={() => setEdu1Visible(false)}
        />
        <Alert
          id="edu2Alert-mobile"
          type="info"
          title="Student Discount Available"
          message={
            <>
              <a href={route('verification.notice')} className="underline">
                Verify your email
              </a>{' '}
              and you'll receive 50% off, courtesy of your university!
            </>
          }
          visible={edu2Visible}
          onClose={() => setEdu2Visible(false)}
        />
        <Alert
          id="payment-mobile"
          type="error"
          title="Finish your payment"
          message={
            <>
              {' '}
              Please{' '}
              <a
                href={route('payment', { id: localStorage.getItem('stripe_id') })}
                className="underline"
              >
                Click here
              </a>{' '}
              to apply your payment to your account!
            </>
          }
          visible={paymentAlertVisible}
          onClose={() => {}}
        />
      </div>
    </>
  );
};

export default AlertsContainer;
